import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { historyApi } from '@/lib/api';
import { fmtDateTime } from '@/lib/date';
import { exportToExcel } from '@/lib/excel';
import { Download } from 'lucide-react';
import { Badge, statusColor } from '@/components/ui/badge';
import { Table, Thead, Tbody, Th, Td, Tr } from '@/components/ui/table';
import { Button } from '@/components/ui/button';
import { PageHeader } from '@/components/ui/page-header';

export function HistoryPage() {
  const navigate = useNavigate();
  const [page, setPage] = useState(1);

  const { data, isLoading } = useQuery({
    queryKey: ['history', page],
    queryFn: () => historyApi.list(page, 50),
  });

  const totalCount = data?.pagination.total ?? 0;

  return (
    <div className="flex flex-col gap-4">
      <PageHeader
        title="Maintenance History"
        tag={totalCount > 0 ? `${totalCount} completed` : undefined}
        actions={
          <Button
            variant="outline" size="sm"
            onClick={() => {
              const rows = (data?.data ?? []).map(wo => ({
                wo_number:   wo.wo_number,
                title:       wo.title,
                equipment:   wo.equipment?.name ?? '',
                task:        wo.maintenance_task?.name ?? '',
                technician:  wo.assigned_technician?.full_name ?? '',
                status:      wo.status,
                completed:   wo.actual_end ?? '',
                labor_hours: wo.labor_hours ?? '',
              }));
              exportToExcel(rows, 'maintenance_history_export');
            }}
          >
            <Download size={14} /> Export
          </Button>
        }
      />

      {isLoading ? (
        <p className="py-8 text-center text-slate-400">Loading…</p>
      ) : (
        <Table variant="cards">
              <Thead>
                <tr>
                  <Th>WO #</Th><Th>Title</Th><Th>Equipment</Th>
                  <Th>Task</Th><Th>Technician</Th>
                  <Th>Completed</Th><Th>Status</Th>
                  <Th>Labor (h)</Th>
                </tr>
              </Thead>
              <Tbody>
                {data?.data.map(wo => (
                  <Tr
                    key={wo.id}
                    onClick={() => navigate(`/work-orders/${wo.id}`)}
                  >
                    <Td className="font-mono text-xs">{wo.wo_number}</Td>
                    <Td className="font-medium text-brand-600">{wo.title}</Td>
                    <Td className="text-xs">{wo.equipment?.name ?? '—'}</Td>
                    <Td className="text-xs">{wo.maintenance_task?.name ?? '—'}</Td>
                    <Td className="text-xs">{wo.assigned_technician?.full_name ?? '—'}</Td>
                    <Td className="text-xs">{fmtDateTime(wo.actual_end)}</Td>
                    <Td><Badge color={statusColor(wo.status)}>{wo.status}</Badge></Td>
                    <Td>{wo.labor_hours ?? '—'}</Td>
                  </Tr>
                ))}
                {data?.data.length === 0 && (
                  <Tr><Td className="py-8 text-center text-slate-400" colSpan={8}>No completed work orders yet.</Td></Tr>
                )}
              </Tbody>
            </Table>
          )}

      {(data?.pagination.total ?? 0) > 50 && (
        <div className="flex justify-end gap-2">
          <Button variant="outline" size="sm" onClick={() => setPage(p => p - 1)} disabled={page === 1}>Prev</Button>
          <Button variant="outline" size="sm" onClick={() => setPage(p => p + 1)} disabled={page * 50 >= (data?.pagination.total ?? 0)}>Next</Button>
        </div>
      )}
    </div>
  );
}
